import React, { useState } from 'react';
import { Shirt } from 'lucide-react';
import type { ProductCardData } from '../../utils/productBlocks';
import { isSafeHttpHref } from '../../utils/connectorCardUrl';

interface ProductTileProps extends ProductCardData {
  brandColor: string;
  isDark: boolean;
  /** Virtual Try-On (Phase 3) — when provided, render a "Try it on" button.
   *  Passed ONLY from the chat widget render path (Widget.tsx) when VTO is
   *  enabled; absent in the admin Inbox and the voice panel, so the button
   *  never appears there. */
  onTryOn?: () => void;
}

const ProductTile: React.FC<ProductTileProps> = ({
  title,
  price,
  image,
  checkout_url,
  brandColor,
  isDark,
  onTryOn,
}) => {
  const [imgError, setImgError] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const validImage = image && image.startsWith('http');
  const cardBg = isDark ? '#1f2937' : '#ffffff';
  const borderColor = isDark ? '#374151' : '#e5e7eb';
  const titleColor = isDark ? '#f3f4f6' : '#111827';
  const priceColor = isDark ? '#4ade80' : '#16a34a';
  const priceBg = isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)';

  // Connector cards (Plan-ConnectorCardRendering) may omit checkout_url — the
  // link hides rather than pointing anywhere dead. Shopify always supplies it;
  // connectors get it from `card_mapping.product_url_template`
  // (Plan-ConnectorProductUrlTemplate). Anything that is not an absolute
  // http(s) URL is discarded here too: a `javascript:`/`data:` value must never
  // reach the DOM, and a relative href would resolve against the widget iframe
  // rather than the store.
  const actionUrl = isSafeHttpHref(checkout_url) ? checkout_url : undefined;
  const highlighted = Boolean(actionUrl) && (hovered || focused);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        // `position: relative` + an explicit z-index scopes the stretched
        // link's stacking to this tile, so its z-indexes can never escape to
        // fight the widget's z-index ladder.
        position: 'relative',
        zIndex: 0,
        width: '128px',
        minWidth: '128px',
        background: cardBg,
        border: `1px solid ${highlighted ? brandColor : borderColor}`,
        borderRadius: '10px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        cursor: actionUrl ? 'pointer' : 'default',
        boxShadow: focused && actionUrl ? `0 0 0 2px ${brandColor}` : 'none',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      }}
    >
      {/* Stretched link — the ONE anchor for the tile. It covers the whole
          card, so the image, title and price are all clickable, while the
          "Try it on" button stays above it (see zIndex below). Rendering the
          visible CTA as an anchor too would nest/duplicate links. */}
      {actionUrl && (
        <a
          href={actionUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`View product: ${title}`}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            borderRadius: '10px',
            outline: 'none',
          }}
        />
      )}

      {/* Square thumbnail */}
      <div
        style={{
          width: '100%',
          aspectRatio: '1',
          background: isDark ? '#111827' : '#f3f4f6',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {validImage && !imgError ? (
          <img
            src={image}
            alt={title}
            onError={() => setImgError(true)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isDark ? '#4b5563' : '#9ca3af',
              fontSize: '24px',
            }}
          >
            &#128722;
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
        <div
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: titleColor,
            lineHeight: '1.3',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: '31px',
          }}
        >
          {title}
        </div>

        {price && (
          <span
            style={{
              fontSize: '12px',
              fontWeight: 700,
              color: priceColor,
              background: priceBg,
              padding: '1px 6px',
              borderRadius: '4px',
              alignSelf: 'flex-start',
            }}
          >
            {price}
          </span>
        )}

        {/* Visible CTA. Not interactive: `pointer-events: none` lets the click
            fall through to the stretched link above, which keeps the tile at a
            single tab stop and the markup free of nested anchors. The link has
            ALWAYS pointed at a product detail page — on Shopify too, where the
            backend builds `/products/{handle}` — so it says "View Product". */}
        {actionUrl && (
          <span
            aria-hidden="true"
            style={{
              marginTop: 'auto',
              fontSize: '11px',
              fontWeight: 600,
              color: '#ffffff',
              background: brandColor,
              padding: '5px 0',
              borderRadius: '6px',
              textAlign: 'center',
              display: 'block',
              pointerEvents: 'none',
              opacity: highlighted ? 0.9 : 1,
              transition: 'opacity 0.15s ease',
            }}
          >
            View Product
          </span>
        )}

        {/* Virtual Try-On (Phase 3) — chat-only; rendered when Widget.tsx
            passes onTryOn (VTO enabled). Sits under View Product, and must
            stay above the stretched link or the tile would swallow its click. */}
        {onTryOn && (
          <button
            type="button"
            onClick={onTryOn}
            style={{
              position: 'relative',
              zIndex: 2,
              marginTop: actionUrl ? '4px' : 'auto',
              fontSize: '11px',
              fontWeight: 600,
              color: brandColor,
              background: 'transparent',
              border: `1px solid ${brandColor}`,
              padding: '4px 0',
              borderRadius: '6px',
              cursor: 'pointer',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              width: '100%',
            }}
          >
            <Shirt size={12} style={{ flexShrink: 0 }} />
            Try it on
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductTile;
